import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import { User, Mail, Shield, Calendar, LogOut } from 'lucide-react';
import { API, authFetch } from '../utils/api';

export default function Profile({ user }) {
  const navigate = useNavigate();
  const [userData, setUserData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    role: user?.role || ''
  });
  const [loading, setLoading] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleSaveProfile = async () => {
    if (!userData.name.trim()) {
      toast.error('Name cannot be empty');
      return;
    }

    setLoading(true);
    try {
      const response = await authFetch(`${API}/users/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: userData.name })
      });

      if (!response.ok) throw new Error('Failed to update profile');

      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await authFetch(`${API}/auth/logout`, {
        method: 'POST',
      });
      localStorage.removeItem('session_token');
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Layout user={user}>
      <div className="p-6 lg:p-12 space-y-8 bg-slate-200 min-h-screen">
        <div>
          <h1 className="text-4xl lg:text-5xl font-heading font-bold tracking-tight" data-testid="profile-title">Profile</h1>
          <p className="text-slate-600 mt-2">Manage your account settings</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Card */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="border border-slate-200 bg-white">
              <CardHeader>
                <CardTitle className="font-heading text-xl uppercase tracking-tight">Account Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center gap-6">
                  <Avatar className="h-24 w-24">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-sky-500 text-white font-bold text-3xl">
                      {user?.name?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-heading text-2xl font-bold">{user?.name}</p>
                    <p className="text-slate-600 uppercase text-sm font-semibold">{user?.role}</p>
                    <p className="text-slate-500 text-sm mt-1">{user?.email}</p>
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-6 space-y-4">
                  <div className="space-y-2">
                    <Label className="uppercase text-xs font-bold tracking-wide flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Full Name
                    </Label>
                    <Input
                      value={userData.name}
                      onChange={(e) => setUserData({ ...userData, name: e.target.value })}
                      className="bg-slate-50 border-slate-200"
                      data-testid="profile-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="uppercase text-xs font-bold tracking-wide flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Email Address
                    </Label>
                    <Input
                      value={userData.email}
                      disabled
                      className="bg-slate-100 border-slate-200"
                      data-testid="profile-email-input"
                    />
                    <p className="text-xs text-slate-500">Email cannot be changed</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="uppercase text-xs font-bold tracking-wide flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      Role
                    </Label>
                    <Input
                      value={userData.role}
                      disabled
                      className="bg-slate-100 border-slate-200 capitalize"
                      data-testid="profile-role-input"
                    />
                    <p className="text-xs text-slate-500">Contact administrator to change role</p>
                  </div>
                </div>

                <Button 
                  className="w-full uppercase font-bold" 
                  onClick={handleSaveProfile}
                  disabled={loading}
                  data-testid="save-profile-button"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Account Stats */}
            <Card className="border border-slate-200 bg-white">
              <CardHeader>
                <CardTitle className="font-heading text-lg uppercase tracking-tight">Account Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="text-sm text-slate-600">User ID</span>
                  <span className="text-sm font-mono font-semibold">{user?.user_id?.slice(-8) || 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="text-sm text-slate-600">Member Since</span>
                  <span className="text-sm font-semibold">{formatDate(user?.created_at)}</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-slate-600">Status</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border border-green-500 text-green-600 bg-green-50">
                    Active
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Danger Zone */}
            <Card className="border border-red-200 bg-red-50/50">
              <CardHeader>
                <CardTitle className="font-heading text-lg uppercase tracking-tight text-red-600">Danger Zone</CardTitle>
              </CardHeader>
              <CardContent>
                <Button 
                  variant="destructive" 
                  className="w-full uppercase font-bold"
                  onClick={handleLogout}
                  data-testid="logout-button"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}
